import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Clock, Calendar, Plus, Save, Trash2, Edit2, BarChart3, TrendingUp, Palmtree, Thermometer, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface EmployeeTimeTrackingFormProps {
  employeeId: string;
  employeeName: string;
  readOnly?: boolean;
}

interface TimeEntry {
  id: string;
  month: string;
  monthly_hours: number;
  vacation_days: number;
  sick_days: number;
  other_absence_days: number;
  other_absence_reason: string | null;
  notes: string | null;
}

interface YearlyStats {
  year: number;
  totalHours: number;
  totalVacation: number;
  totalSick: number;
  totalOther: number;
  monthCount: number;
  avgHoursPerMonth: number;
  totalAbsenceDays: number;
}

export function EmployeeTimeTrackingForm({ employeeId, employeeName, readOnly = false }: EmployeeTimeTrackingFormProps) {
  const { user, userRole } = useAuth();
  const isReadOnly = readOnly || userRole === 'employee';
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("all");
  
  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  
  const [formData, setFormData] = useState({
    month: currentMonth,
    monthly_hours: 0,
    vacation_days: 0,
    sick_days: 0,
    other_absence_days: 0,
    other_absence_reason: "",
    notes: ""
  });

  // Calculate yearly statistics
  const yearlyStats = useMemo(() => {
    const statsByYear: Record<number, YearlyStats> = {};
    
    entries.forEach(entry => {
      const year = parseInt(entry.month.split('-')[0]);
      
      if (!statsByYear[year]) {
        statsByYear[year] = {
          year,
          totalHours: 0,
          totalVacation: 0,
          totalSick: 0,
          totalOther: 0,
          monthCount: 0,
          avgHoursPerMonth: 0,
          totalAbsenceDays: 0
        };
      }
      
      statsByYear[year].totalHours += entry.monthly_hours || 0;
      statsByYear[year].totalVacation += entry.vacation_days || 0;
      statsByYear[year].totalSick += entry.sick_days || 0;
      statsByYear[year].totalOther += entry.other_absence_days || 0;
      statsByYear[year].monthCount += 1;
    });
    
    // Calculate averages and totals
    Object.values(statsByYear).forEach(stats => {
      stats.avgHoursPerMonth = stats.monthCount > 0 ? stats.totalHours / stats.monthCount : 0;
      stats.totalAbsenceDays = stats.totalVacation + stats.totalSick + stats.totalOther;
    });
    
    return Object.values(statsByYear).sort((a, b) => b.year - a.year);
  }, [entries]);

  // Get available years for filter
  const availableYears = useMemo(() => {
    const years = [...new Set(entries.map(e => e.month.split('-')[0]))];
    return years.sort((a, b) => parseInt(b) - parseInt(a));
  }, [entries]);

  // Filter entries by selected year
  const filteredEntries = useMemo(() => {
    if (selectedYear === "all") return entries;
    return entries.filter(e => e.month.startsWith(selectedYear));
  }, [entries, selectedYear]);

  useEffect(() => {
    fetchEntries();
  }, [employeeId]);

  const fetchEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('employee_time_tracking')
        .select('*')
        .eq('employee_id', employeeId)
        .order('month', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching time tracking:', error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      if (editingId) {
        const { error } = await supabase
          .from('employee_time_tracking')
          .update({
            monthly_hours: formData.monthly_hours,
            vacation_days: formData.vacation_days,
            sick_days: formData.sick_days,
            other_absence_days: formData.other_absence_days,
            other_absence_reason: formData.other_absence_reason || null,
            notes: formData.notes || null
          })
          .eq('id', editingId);

        if (error) throw error;
        toast.success("Eintrag aktualisiert");
      } else {
        const { error } = await supabase
          .from('employee_time_tracking')
          .insert({
            employee_id: employeeId,
            month: formData.month,
            monthly_hours: formData.monthly_hours,
            vacation_days: formData.vacation_days,
            sick_days: formData.sick_days,
            other_absence_days: formData.other_absence_days,
            other_absence_reason: formData.other_absence_reason || null,
            notes: formData.notes || null,
            created_by: user.id
          });

        if (error) {
          if (error.code === '23505') {
            toast.error("Für diesen Monat existiert bereits ein Eintrag");
            return;
          }
          throw error;
        }
        toast.success("Eintrag hinzugefügt");
      }

      resetForm();
      fetchEntries();
    } catch (error) {
      console.error('Error saving time tracking:', error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleEdit = (entry: TimeEntry) => {
    setFormData({
      month: entry.month,
      monthly_hours: entry.monthly_hours,
      vacation_days: entry.vacation_days,
      sick_days: entry.sick_days,
      other_absence_days: entry.other_absence_days,
      other_absence_reason: entry.other_absence_reason || "",
      notes: entry.notes || ""
    });
    setEditingId(entry.id);
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('employee_time_tracking')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success("Eintrag gelöscht");
      fetchEntries();
    } catch (error) {
      console.error('Error deleting time tracking:', error);
      toast.error("Fehler beim Löschen");
    }
  };

  const resetForm = () => {
    setFormData({
      month: currentMonth,
      monthly_hours: 0,
      vacation_days: 0,
      sick_days: 0,
      other_absence_days: 0,
      other_absence_reason: "",
      notes: ""
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const generateMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  const formatMonth = (month: string) => {
    const [year, m] = month.split('-');
    return new Date(parseInt(year), parseInt(m) - 1, 1).toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Stunden & Abwesenheit</h3>
        </div>
        {!isAdding && (
          <Button onClick={() => setIsAdding(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Neuer Eintrag
          </Button>
        )}
      </div>

      {isAdding && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {editingId ? "Eintrag bearbeiten" : "Neuer Eintrag"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Monat</Label>
                <Select
                  value={formData.month}
                  onValueChange={(value) => setFormData({ ...formData, month: value })}
                  disabled={!!editingId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Monat wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {generateMonthOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Monatsstunden</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.monthly_hours}
                  onChange={(e) => setFormData({ ...formData, monthly_hours: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Urlaubstage</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.vacation_days}
                  onChange={(e) => setFormData({ ...formData, vacation_days: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Krankheitstage</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.sick_days}
                  onChange={(e) => setFormData({ ...formData, sick_days: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Sonstige Fehltage</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.other_absence_days}
                  onChange={(e) => setFormData({ ...formData, other_absence_days: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Grund (sonstige Fehltage)</Label>
                <Input
                  value={formData.other_absence_reason}
                  onChange={(e) => setFormData({ ...formData, other_absence_reason: e.target.value })}
                  placeholder="z.B. Weiterbildung, Sonderurlaub..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Zusätzliche Anmerkungen..."
                rows={2}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={resetForm}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} className="gap-2">
                <Save className="h-4 w-4" />
                Speichern
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jahresübersicht */}
      {yearlyStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Jahresübersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {yearlyStats.map((stats) => (
                <div key={stats.year} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-lg">{stats.year}</h4>
                    <span className="text-sm text-muted-foreground">
                      {stats.monthCount} {stats.monthCount === 1 ? 'Monat' : 'Monate'} erfasst
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="bg-primary/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <TrendingUp className="h-4 w-4" />
                        Gesamtstunden
                      </div>
                      <p className="text-xl font-bold">{stats.totalHours.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">
                        Ø {stats.avgHoursPerMonth.toFixed(1)} / Monat
                      </p>
                    </div>
                    
                    <div className="bg-green-500/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Palmtree className="h-4 w-4 text-green-600" />
                        Urlaub
                      </div>
                      <p className="text-xl font-bold text-green-600">{stats.totalVacation}</p>
                      <p className="text-xs text-muted-foreground">Tage</p>
                    </div>
                    
                    <div className="bg-orange-500/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Thermometer className="h-4 w-4 text-orange-600" />
                        Krankheit
                      </div>
                      <p className="text-xl font-bold text-orange-600">{stats.totalSick}</p>
                      <p className="text-xs text-muted-foreground">Tage</p>
                    </div>
                    
                    <div className="bg-purple-500/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <CalendarOff className="h-4 w-4 text-purple-600" />
                        Sonstige
                      </div>
                      <p className="text-xl font-bold text-purple-600">{stats.totalOther}</p>
                      <p className="text-xs text-muted-foreground">Fehltage</p>
                    </div>
                    
                    <div className="bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Calendar className="h-4 w-4" />
                        Gesamt Abwesend
                      </div>
                      <p className="text-xl font-bold">{stats.totalAbsenceDays}</p>
                      <p className="text-xs text-muted-foreground">Tage</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year filter */}
      {entries.length > 0 && availableYears.length > 1 && (
        <div className="flex items-center gap-2">
          <Label>Filter nach Jahr:</Label>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Jahre</SelectItem>
              {availableYears.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Monatliche Einträge */}
      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Noch keine Einträge vorhanden</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monatliche Einträge</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Monat</TableHead>
                    <TableHead className="text-right">Stunden</TableHead>
                    <TableHead className="text-right">Urlaub</TableHead>
                    <TableHead className="text-right">Krank</TableHead>
                    <TableHead className="text-right">Sonstige</TableHead>
                    <TableHead>Grund</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">{formatMonth(entry.month)}</TableCell>
                      <TableCell className="text-right">{entry.monthly_hours}</TableCell>
                      <TableCell className="text-right">{entry.vacation_days}</TableCell>
                      <TableCell className="text-right">{entry.sick_days}</TableCell>
                      <TableCell className="text-right">{entry.other_absence_days}</TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {entry.other_absence_reason || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(entry)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
